/**
 * Utilities for navigating Facebook's album photo view.
 */

import {
  PrivacySetting,
  clickAndWaitForModalToOpen,
  clickRowMatchingPrivacySetting,
  closeModal,
  getEditPrivacyIcons,
  getSelectedPrivacyLevel,
  isPrivacySettingAsStrictOrStricter,
  isPrivacySettingUndoable,
} from './fbSelectAudienceModalUtils'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import * as promises from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'

export class FacebookAlbumPhotoViewUtilsException extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FacebookAlbumPhotoViewUtilsException'
  }
}

// Identifying photos

export function getPhotoIdFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    const photoId = urlObj.searchParams.get('fbid')
    return photoId
  } catch {
    return null
  }
}

export function getPhotoIdFromCurrentUrl(): string | null {
  return getPhotoIdFromUrl(window.location.href)
}

export function getVisiblePhotoUrls() {
  const visibleHrefNodes = evaluateXpath(document, '//*[@href]/@href')

  if (!visibleHrefNodes) {
    throw new FacebookAlbumPhotoViewUtilsException(
      'Could not find href nodes for Facebook photo albums',
    )
  }

  const isPhotoUrlPat = /^\/photo\/\?fbid/
  const visiblePhotoUrls = visibleHrefNodes
    .map((hrefNode) => hrefNode?.textContent)
    .filter((hrefNode) => isPhotoUrlPat.test(hrefNode))

  return Array.from(new Set(visiblePhotoUrls))
}

// Navigate

/**
 * Wait for albums tab to load; for use https://www.facebook.com/{username}/photos_albums
 */
export async function waitForAlbumsToLoad(
  options: { timeoutMs?: number } = {},
): Promise<void> {
  return promises
    .getWaitForElementToAppearPromise(
      document,
      (node) => evaluateXpath(node, '//*[text() = "Albums"]')[0],
      options?.timeoutMs,
    )
    .then(() => {})
}

/**
 * From the albums view (e.g. https://www.facebook.com/{username}/photos_albums),
 * navigate to a photo album with a particular name.
 */
function __getAlbumTile(albumName: string): HTMLElement | undefined {
  return evaluateXpath<HTMLElement>(document, `//*[text() = '${albumName}']`)[0]
}

export function hasAlbum(albumName: string): boolean {
  return Boolean(__getAlbumTile(albumName))
}

export function goToAlbum(albumName: string): void {
  const albumTile = __getAlbumTile(albumName)

  if (!albumTile) {
    throw new FacebookAlbumPhotoViewUtilsException(
      `Could not find album with name ${albumName}`,
    )
  }

  albumTile.click()
}

/**
 * From the photo slideshow view within the album page, click a photo by id
 */
export async function clickOnPhotoById(photoId): Promise<void> {
  function matchesPhotoId(href: string): boolean {
    const fbidParam = `fbid=${photoId}`
    const idx = href.indexOf(fbidParam)
    if (idx === -1) return false
    const charAfter = href[idx + fbidParam.length]
    return charAfter === undefined || charAfter === '&'
  }

  function getPhotoAnchorElement(node: Node): HTMLAnchorElement | undefined {
    const candidatePhotos = evaluateXpath<HTMLAnchorElement>(
      node,
      `//a[contains(@href, "fbid=${photoId}")]`,
    )
    return candidatePhotos.filter((candidate) =>
      matchesPhotoId(candidate.href),
    )[0]
  }

  const photoAnchorElement = await promises.getWaitForElementToAppearPromise(
    document,
    getPhotoAnchorElement,
  )

  if (!photoAnchorElement) {
    throw new FacebookAlbumPhotoViewUtilsException(
      'Could not find photo anchor element',
    )
  }

  photoAnchorElement.click()
}

export async function scrollToBottom(): Promise<void> {
  let numTryNoChange = 0
  let prevScrollHeight = window.scrollY
  while (numTryNoChange < 6) {
    window.scrollBy(0, 1000)
    await promises.wait(500)

    if (window.scrollY === prevScrollHeight) {
      numTryNoChange += 1
    } else {
      numTryNoChange = 0
    }

    prevScrollHeight = window.scrollY

    throwIfGlobalInterruptRequested()
  }
}

// Privacy helpers

export async function gatherEditPrivacyIconValue(): Promise<PrivacySetting | null> {
  // Find edit audience icon, waiting if needed
  const editPrivacyIcon = await Promise.race([
    promises.getWaitForElementToAppearPromise<HTMLElement>(document, (node) => {
      const icons = getEditPrivacyIcons(node)
      return icons && icons.length ? icons[0] : null
    }),
    promises.throwIfTimeout(
      'Timed out looking for element for edit audience icon',
      5000,
    ),
  ])

  await clickAndWaitForModalToOpen(editPrivacyIcon)

  const privacySetting = await getSelectedPrivacyLevel()

  await closeModal('CANCEL', { removeModalManually: true })

  return privacySetting
}

export async function setEditPrivacyIconValueAsStrictOrStricter(
  targetSetting: PrivacySetting,
  targetSettingSecondOption: PrivacySetting | null,
  isDry: boolean,
): Promise<void> {
  // Short-circuit if this isn't a setting we can handle
  if (!isPrivacySettingUndoable(targetSetting)) {
    return
  }

  // Find edit audience icon, waiting if needed
  const editPrivacyIcon = await Promise.race([
    promises.getWaitForElementToAppearPromise<HTMLElement>(document, (node) => {
      const icons = getEditPrivacyIcons(node)
      return icons && icons.length ? icons[0] : null
    }),
    promises.throwIfTimeout(
      'Timed out getting element for edit audience icon',
      5000,
    ),
  ])

  // Open modal, select target settting, click done
  await clickAndWaitForModalToOpen(editPrivacyIcon)

  const curSetting = await getSelectedPrivacyLevel()
  if (isPrivacySettingAsStrictOrStricter(curSetting, targetSetting)) {
    await closeModal('CANCEL')
  } else {
    // Explicitly for cases like "Friends" where sometimes the option
    // does not exist; we then try the second option if one is provided.
    try {
      await clickRowMatchingPrivacySetting(targetSetting)
    } catch {
      if (targetSettingSecondOption) {
        await clickRowMatchingPrivacySetting(targetSettingSecondOption)
      }
    }
    await closeModal(isDry ? 'CANCEL' : 'DONE')
  }
}

export async function setEditPrivacyIconValueExact(
  targetSetting: PrivacySetting,
  isDry: boolean,
): Promise<void> {
  // Short-circuit if this isn't a setting we can handle
  if (!isPrivacySettingUndoable(targetSetting)) {
    return
  }

  // Find edit audience icon, waiting if needed
  const editPrivacyIcon =
    await promises.getWaitForElementToAppearPromise<HTMLElement>(
      document,
      (node) => {
        const icons = getEditPrivacyIcons(node)
        return icons && icons.length ? icons[0] : null
      },
      5000,
      true,
    )

  // Open modal, select target settting, click done
  await clickAndWaitForModalToOpen(editPrivacyIcon)

  const curSetting = await getSelectedPrivacyLevel()
  if (
    curSetting.level === targetSetting.level &&
    curSetting.name === targetSetting.name
  ) {
    await closeModal('CANCEL')
  } else {
    await clickRowMatchingPrivacySetting(targetSetting)
    await closeModal(isDry ? 'CANCEL' : 'DONE')
  }
}
